import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { admin, getImageUrl } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, RefreshCw, FolderOpen, Upload, ImagePlus, X } from 'lucide-react';

const AdminCategories = () => {
  usePageTitle('Manage Categories');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Dialog states
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await admin.getCategories();
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug || generateSlug(value)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);
    toast.info(`Selected: ${file.name}`);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    toast.loading('Uploading image...', { id: 'upload-toast' });
    
    try {
      console.log('Starting upload...');
      const result = await admin.uploadGeneralImage(file);
      console.log('Upload result:', result);
      
      if (result && result.image_url) {
        const imageUrl = getImageUrl(result.image_url);
        console.log('Final image URL:', imageUrl);
        setFormData(prev => ({ ...prev, image: imageUrl }));
        toast.success('Image uploaded successfully!', { id: 'upload-toast' });
      } else {
        throw new Error('No image URL returned');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image', { id: 'upload-toast' });
    } finally {
      setUploading(false);
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  const openCreateDialog = () => {
    setSelectedCategory(null);
    setFormData({ name: '', slug: '', description: '', image: '' });
    setEditDialog(true);
  };

  const openEditDialog = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image: category.image || ''
    });
    setEditDialog(true);
  };

  const openDeleteDialog = (category) => {
    setSelectedCategory(category);
    setDeleteDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (selectedCategory) {
        // Update
        await admin.updateCategory(selectedCategory.id, formData);
        toast.success('Category updated');
      } else {
        // Create
        await admin.createCategory(formData);
        toast.success('Category created');
      }
      setEditDialog(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    
    setSaving(true);
    try {
      await admin.deleteCategory(selectedCategory.id);
      toast.success('Category deleted');
      setDeleteDialog(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      await admin.updateCategory(category.id, { is_active: !category.is_active });
      toast.success(category.is_active ? 'Category disabled' : 'Category enabled');
      loadCategories();
    } catch (error) {
      toast.error('Failed to update category status');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-[400px] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Category Management</h1>
          <p className="text-gray-600">Manage product categories</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadCategories} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="bg-[#002FA7]">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(category => (
          <div 
            key={category.id} 
            className={`border rounded-lg p-4 ${!category.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {category.image ? (
                  <img 
                    src={category.image}
                    alt={category.name}
                    className="w-12 h-12 rounded object-cover bg-gray-100 flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{category.slug}</p>
                </div>
              </div>
              {!category.is_active && (
                <Badge variant="outline" className="text-red-600 border-red-200">Disabled</Badge>
              )}
            </div>
            
            {category.description && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{category.description}</p>
            )}
            
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Package className="w-4 h-4" />
                <span>{category.product_count || 0} products</span>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => toggleCategoryStatus(category)}
                >
                  {category.is_active ? 'Disable' : 'Enable'}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openEditDialog(category)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openDeleteDialog(category)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={category.product_count > 0}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 border rounded-lg bg-gray-50">
          <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No categories yet</h3>
          <p className="text-gray-500 mb-4">Create your first category to organize products</p>
          <Button onClick={openCreateDialog} className="bg-[#002FA7]">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCategory ? 'Edit Category' : 'Create Category'}
            </DialogTitle>
            <DialogDescription>
              {selectedCategory 
                ? 'Update the category details below' 
                : 'Fill in the details to create a new category'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., Sweatshirts"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="e.g., sweatshirts"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                URL-friendly identifier. Auto-generated from name if left empty.
              </p>
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this category..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="image">Category Image</Label>
              
              {/* Image Preview */}
              {formData.image && (
                <div className="mt-2 relative inline-block">
                  <img 
                    src={formData.image} 
                    alt="Preview" 
                    className="w-32 h-32 object-cover rounded border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-500 hover:bg-red-600 text-white"
                    onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              {/* Upload Options */}
              <div className="mt-2 flex flex-col gap-2">
                <input
                  type="file"
                  onChange={handleImageUpload}
                  accept="image/*"
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer cursor-pointer"
                />
                {uploading && (
                  <p className="text-sm text-blue-600 animate-pulse">Uploading your image...</p>
                )}
              </div>
              
              {/* URL Input */}
              <div className="mt-3">
                <Label htmlFor="image-url" className="text-xs text-gray-500">Or paste image URL directly</Label>
                <Input
                  id="image-url"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#002FA7]"
            >
              {saving ? 'Saving...' : selectedCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedCategory?.name}&quot;? 
              This action cannot be undone.
              {selectedCategory?.product_count > 0 && (
                <span className="block mt-2 text-red-600">
                  This category has {selectedCategory.product_count} products. 
                  Please reassign them before deleting.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving || (selectedCategory?.product_count > 0)}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategories;
